<script setup lang="ts">
import { ref, computed } from 'vue';
import { debugLog, clearDebugLog } from '@/utils/onScreenLog';

/**
 * On-screen debug log: read [LiveRadar] traces inside the Mini App without DevTools.
 * The log itself lives in @/utils/onScreenLog (shared with live-main.ts so boot-time and
 * global errors are captured too); this panel only owns its UI state.
 */

const showDebug = ref(false);
const debugCopied = ref(false);
/** Only errors are surfaced on the floating button — that's the signal worth noticing at a glance. */
const debugErrorCount = computed(() =>
  debugLog.value.reduce((n, e) => n + (e.level === 'error' ? e.count : 0), 0),
);

async function copyDebugLog(): Promise<void> {
  const text = debugLog.value
    .map((e) => `${e.t} [${e.level}] ${e.msg}${e.count > 1 ? ` ×${e.count}` : ''}`)
    .join('\n');
  try {
    await navigator.clipboard.writeText(text);
    debugCopied.value = true;
    setTimeout(() => (debugCopied.value = false), 1500);
  } catch {
    /* clipboard unavailable — user can still read/select the panel */
  }
}
</script>

<template>
  <!-- The floating button is always visible so it works even if the map/layout is broken. -->
  <button
    type="button"
    class="debug-fab"
    :class="{ active: showDebug }"
    :title="showDebug ? 'скрыть лог' : 'показать лог диагностики'"
    @click="showDebug = !showDebug"
  >
    🐞<span
      v-if="debugErrorCount"
      class="debug-fab-count"
    >{{ debugErrorCount }}</span>
  </button>

  <div
    v-if="showDebug"
    class="debug-panel"
  >
    <div class="debug-head">
      <span class="debug-title">Диагностика ({{ debugLog.length }})</span>
      <button
        type="button"
        class="debug-act"
        @click="copyDebugLog"
      >
        {{ debugCopied ? '✅ скопировано' : '📋 копировать' }}
      </button>
      <button
        type="button"
        class="debug-act"
        @click="clearDebugLog"
      >
        🗑 очистить
      </button>
      <button
        type="button"
        class="debug-act debug-close"
        @click="showDebug = false"
      >
        ✕
      </button>
    </div>
    <div class="debug-body">
      <div
        v-if="!debugLog.length"
        class="debug-empty"
      >
        Логов пока нет.
      </div>
      <div
        v-for="(e, i) in debugLog"
        :key="i"
        class="debug-line"
        :class="e.level"
      >
        <span class="debug-time">{{ e.t }}</span> {{ e.msg }}<span
          v-if="e.count > 1"
          class="debug-repeat"
        > ×{{ e.count }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.debug-fab {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 1000;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 20, 32, 0.85);
  border: 1px solid #2a3346;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  touch-action: manipulation;
}
.debug-fab.active {
  background: #2aabee;
  border-color: #2aabee;
}
.debug-fab-count {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #c0392b;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
}
.debug-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  max-height: 55vh;
  display: flex;
  flex-direction: column;
  background: rgba(8, 11, 18, 0.97);
  border-top: 1px solid #2a3346;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.6);
}
.debug-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #1c2638;
  flex-shrink: 0;
}
.debug-title {
  font-size: 12px;
  font-weight: 700;
  color: #9bd6f7;
  margin-right: auto;
}
.debug-act {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #2a3346;
  background: #161e2c;
  color: #cdd5e3;
  font-size: 12px;
  cursor: pointer;
  /* Fire the click immediately on touch — no double-tap-zoom delay inside mobile WebViews. */
  touch-action: manipulation;
}
.debug-close {
  /* The close button must be the easiest thing to hit on a phone. */
  min-width: 40px;
  font-size: 14px;
}
.debug-body {
  overflow-y: auto;
  padding: 8px 10px 14px;
  -webkit-overflow-scrolling: touch;
}
.debug-empty {
  color: #6f7a90;
  font-size: 12px;
  text-align: center;
  padding: 20px;
}
.debug-line {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.5;
  color: #cdd5e3;
  white-space: pre-wrap;
  word-break: break-word;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  padding: 2px 0;
  /* Let the user select/copy lines by hand if the clipboard API is unavailable. */
  user-select: text;
}
.debug-line.warn {
  color: #f1c40f;
}
.debug-line.error {
  color: #ff8a8a;
}
.debug-time {
  color: #6f7a90;
}
.debug-repeat {
  color: #f1c40f;
  font-weight: 700;
}
</style>
